import { css } from '@emotion/react';
import { Box, CircularProgress, Grid } from '@material-ui/core';
import { format } from 'date-fns';
import assign from 'lodash/assign';
import difference from 'lodash/difference';
import first from 'lodash/first';
import flow from 'lodash/flow';
import fp_get from 'lodash/fp/get';
import isArray from 'lodash/isArray';
import isObject from 'lodash/isObject';
import not from 'lodash/negate';
import pickBy from 'lodash/pickBy';
import transform from 'lodash/transform';

import { applicationApi } from 'lib/api/applicationApi';
import { i18n } from '../../../../../lib/i18n';
import { LeagueCheckoutStore } from '../../../../../stores/leagueCheckoutStore';
import { RentalCheckoutStore } from '../../../../../stores/rentalCheckoutStore';

import {
	AddressAnswer as IAddressAnswer,
	AddressAnswerKey,
	Answer,
	AnswerValue,
	ApplicationWaiver,
} from '../../../../../types/answer';
import { Area, CheckoutStepsEnum, FlowStep, ProgramCheckoutStore } from '../../../../../types/checkout';
import { Product, ProductPackage, ProductSettingsKeysEnum } from '../../../../../types/product';
import { Questions, QuestionsTypeEnum } from '../../../../../types/questions';

import Alert from '../../../../shared/Alert/Alert';
import { PRODUCT_SCHEDULE_KEYS } from '../../lib/checkout.consts';
import { UserSettings } from 'types/userSettings';
import sortBy from 'lodash/sortBy';
import { BooleanString } from 'types/enums';

const alertCss = css`
	&.MuiPaper-root {
		box-shadow: none;
	}
`;

export type formValues = { [x: string]: never };

export const FormUtils = {
	getAnswer,
	getAddressAnswer: getAddressField,
	fetchQuestionData,
	getSelectedAnswers,
};
export const prefix = 'question_';

export enum FormAnswerFieldEnum {
	date = 'date',
	userAddressStreet = 'userAddressStreet',
	userAddressZip = 'userAddressZip',
	userAddressCity = 'userAddressCity',
	userAddressState = 'userAddressState',
}
export declare type FormAnswerField = keyof typeof FormAnswerFieldEnum;
export declare type FormAddressField = Extract<
	FormAnswerField,
	'userAddressStreet' | 'userAddressZip' | 'userAddressCity' | 'userAddressState'
>;

export type IProgressStep = keyof typeof i18n.checkout.progress | FlowStep;

async function fetchQuestionData(organizationId: number, questionnaireId: number) {
	const response = await applicationApi.getQuestionData(organizationId, questionnaireId);

	let questions: Questions[] = [];
	let termsQuestions: Questions[] = [];
	const title = response.title || '';

	if (Array.isArray(response?.questions)) {
		questions = [...response.questions];
	}

	// remove all inactive question
	questions = questions.filter(question => question.isActive);

	// set left column with WAIVER and CUSTOM_WAIVER
	termsQuestions = questions.filter(
		question =>
			question.questionType === QuestionsTypeEnum.WAIVER || question.questionType === QuestionsTypeEnum.CUSTOM_WAIVER
	);

	const noWaivers = difference(questions, termsQuestions);

	// return state information
	return {
		noWaivers: sortBy(noWaivers, q => q.ordinal),
		termsQuestions: sortBy(termsQuestions, q => q.ordinal),
		qId: questionnaireId,
		title,
	};
}

function getSelectedAnswers(formValues: formValues) {
	// add normalized values
	const entries = Object.entries(formValues);

	const selectedAnswers: Answer[] = entries
		/* eslint-disable unicorn/no-fn-reference-in-iterator */
		.filter(not(isAddressEntry))
		.map(entry => getAnswer(entry));

	const addressEntries = entries.filter(isAddressEntry);
	if (addressEntries.length) {
		const address = Address.getAddressAnswer(addressEntries);
		selectedAnswers.push(address);
	}
	return selectedAnswers;
}

function isAddressFieldName(fieldName: string) {
	switch (fieldName) {
		case FormAnswerFieldEnum.userAddressStreet:
		case FormAnswerFieldEnum.userAddressZip:
		case FormAnswerFieldEnum.userAddressCity:
		case FormAnswerFieldEnum.userAddressState:
			return true;
	}

	return false;
}

function getAnswer([formField, fieldValue]: [string, never]): Answer {
	const { fieldName, questionId } = parseFormField(formField);
	let value: AnswerValue;

	switch (fieldName) {
		case FormAnswerFieldEnum.date:
			value = { value: format(new Date(fieldValue), 'yyyy-MM-dd') };
			break;

		default:
			if (isArray(fieldValue)) {
				(fieldValue as never[]) = [...fieldValue]; // why copy?
			}
			value = { value: fieldValue };
	}

	return { questionId, value };
}

/**
 * @param fieldValue
 * @param fieldName
 */
function getAddressField(fieldName: string, fieldValue: never): Partial<IAddressAnswer> {
	const addressObj: { [x: string]: string } = {};
	const [addressName] = fieldName.split('_');
	const prop = addressName.toLowerCase().replace('useraddress', '');
	addressObj[prop as AddressAnswerKey] = fieldValue;

	return addressObj;
}

function parseFormField(formField: string) {
	const [fieldName, sQuestionId] = formField.split('_');
	const questionId = Number(sQuestionId);
	return { fieldName, questionId, sQuestionId };
}

const isAddressEntry = flow(first, parseFormField, fp_get('fieldName'), isAddressFieldName);

/* eslint-disable fp/no-class */
class Address implements IAddressAnswer {
	street = '';
	city = '';
	state = '';
	zip = '';

	static fromEntries(entries: [string, never][]) {
		/* eslint-disable fp/no-this */
		const addressObj = new this();

		transform(
			entries,
			(addressObj, entry) => {
				const answer = getAddressField(...entry);
				const value = pickBy(answer, Boolean);
				assign(addressObj, value);
			},
			addressObj
		);

		return addressObj;
	}

	static getAddressAnswer(entries: [string, never][]) {
		/* eslint-disable fp/no-this */
		const value = this.fromEntries(entries);
		const flatEntries = entries.map(entry => {
			if (Array.isArray(entry)) {
				return entry[0];
			}
			return entry;
		});

		const questionId: number = flow(first, parseFormField, fp_get('questionId'))(flatEntries);

		return { questionId, value };
	}
}

const checkDisabledCheckoutSteps = (checkoutState: RentalCheckoutStore, step: CheckoutStepsEnum) => {
	return checkoutState?.skipCheckoutSteps?.includes(step);
};

const getIsDisabledStep = (step: string, checkoutState?: RentalCheckoutStore) => {
	if (!checkoutState) {
		return false;
	}
	return checkDisabledCheckoutSteps(checkoutState, step.toLowerCase() as CheckoutStepsEnum);
};

export function checkoutFlowRawFactory(
	area: Area,
	steps: IProgressStep[],
	checkoutState?: RentalCheckoutStore
): FlowStep[] {
	const mapSteps = (step: IProgressStep): FlowStep => {
		if (isObject(step)) {
			const isDisabledStep = getIsDisabledStep(step?.tabName, checkoutState);
			return { ...step, isDisabled: isDisabledStep };
		}
		return {
			tabName: i18n.checkout.progress[step], // e.g i18n.checkout.progress.payment
			urlPath: `${area}/${step}`, // e.g program/payment
			isDisabled: getIsDisabledStep(step, checkoutState),
		};
	};

	return steps.map(step => mapSteps(step));
}

// inner components
export function ProgressIcon() {
	return (
		<Grid container justify="center">
			<CircularProgress />
		</Grid>
	);
}

export function AlertBox({ message }: { message: string }) {
	return (
		<Box mb={2}>
			<Alert css={alertCss} severity="error">
				{message}
			</Alert>
		</Box>
	);
}

export interface IStepProps {
	area?: Area;
}

interface ConditionalProps {
	show?: boolean;
	child?: React.ReactNode;
	children?: React.ReactNode;
}

/**
 * Conditionally show React Elements
 *
 * @param props
 * @param {boolean} props.show - true if children should render, false otherwise
 * @param {React.ReactNode} props.child - override children if provided
 */
export function Conditional(props: ConditionalProps) {
	if (!props.show) {
		return null;
	}

	return <>{(props.child && props.child) || (props.children && props.children)}</>;
}

export type UseProductsStore = ProgramCheckoutStore | LeagueCheckoutStore;

export const checkIfShowSchedule = (product?: Product) => {
	const isSkipSetting =
		product?.resourceSettings?.some(settingObj => {
			if ((PRODUCT_SCHEDULE_KEYS as string[]).includes(settingObj.key)) {
				// e.g. { key: isHidePunchPassSchedule, value: 'true' }
				return settingObj.value === BooleanString.True;
			}

			return false;
		}) ?? false;
	// This function is to show, not skip. So we need to negate the result of isSkipSetting
	return !isSkipSetting;
};

export const checkIfMultipleQuantitiesPurchaseAllowed = (product?: Product) => {
	return product?.resourceSettings?.find(setting => setting.key === ProductSettingsKeysEnum.IS_MULTIPLE_QUANTITIES_PURCHASE_ALLOWED)?.value === BooleanString.True;
};

export const checkIfShowAddons = (prodPackage?: ProductPackage) => {
	const addons = prodPackage?.children ?? [];
	return Boolean(addons.length);
};

export const getStepsToSkip = (product?: Product, prodPackage?: ProductPackage) => {
	const stepsToSkip: CheckoutStepsEnum[] = [];
	if (!checkIfShowSchedule(product)) {
		stepsToSkip.push(CheckoutStepsEnum.SCHEDULE);
	}

	if (!checkIfShowAddons(prodPackage)) {
		stepsToSkip.push(CheckoutStepsEnum.ADDONS);
	}

	return stepsToSkip;
};

type TFormValues = { [y: string]: { [x: string]: unknown } };

/**
 * Converts form values and selected family member into an array of ApplicationWaiver objects.
 * @param formValues - The form values.
 * @param selectedFamilyMembers - The selected family member or an array of selected family members.
 * @returns An array of ApplicationWaiver objects.
 */
export const getAnswersAsUserAnswersDto = (
	formValues: TFormValues,
	selectedFamilyMembers: UserSettings | UserSettings[]
): ApplicationWaiver[] => {
	if (Array.isArray(selectedFamilyMembers) && selectedFamilyMembers.length > 1) {
		const finalAnswers: ApplicationWaiver[] = [];
		Object.keys(formValues).forEach(userId => {
			const selectedAnswers = retrieveAnswers(formValues[userId]);
			const answers: ApplicationWaiver = {
				userId: Number(userId),
				answers: [...selectedAnswers],
			};
			finalAnswers.push(answers);
		});

		return finalAnswers;
	}
	const selectedAnswers = retrieveAnswers(formValues);
	const selectedFamilyMemberId = Array.isArray(selectedFamilyMembers)
		? selectedFamilyMembers[0].id
		: selectedFamilyMembers.id;
	return [{ userId: selectedFamilyMemberId, answers: selectedAnswers }];
};

// eslint-disable-next-line  @typescript-eslint/no-explicit-any
export const retrieveAnswers = (formValues: { [x: string]: any }) => {
	let addressObj = {
		questionId: 0,
		street: '',
		city: '',
		state: '',
		zip: '',
	};
	const selectedAnswers: Answer[] = [];
	for (const formField of Object.keys(formValues)) {
		const [fieldName, sQuestionId] = formField.split('_');
		const questionId = Number(sQuestionId);
		const fieldValue = formValues[formField];

		switch (fieldName) {
			case 'date':
				selectedAnswers.push({
					questionId,
					value: format(fieldValue, 'yyyy-MM-dd'),
				});
				break;

			case 'userAddressStreet':
				addressObj = { ...addressObj, questionId, street: fieldValue };
				break;

			case 'userAddressZip':
				addressObj = { ...addressObj, questionId, zip: fieldValue };
				break;

			case 'userAddressCity':
				addressObj = { ...addressObj, questionId, city: fieldValue };
				break;

			case 'userAddressState':
				addressObj = { ...addressObj, questionId, state: fieldValue };
				break;

			default:
				if (Array.isArray(fieldValue)) {
					selectedAnswers.push({
						questionId,
						value: { value: [...fieldValue] },
					});
				} else {
					selectedAnswers.push({
						questionId,
						value: { value: fieldValue },
					});
					break;
				}
		}
	}

	if (addressObj.questionId) {
		const zip = addressObj.zip;
		selectedAnswers.push({
			questionId: addressObj.questionId,
			value: {
				street: addressObj.street,
				city: addressObj.city,
				state: addressObj.state,
				zip,
			},
		});
	}

	return selectedAnswers;
};

export const updatedFormValues = (formValues: { [x: string]: any }) =>
	Object.keys(formValues).reduce((acc: { [x: string]: any }, key: string) => {
		const parts = key.split('_');
		const id = parts.length > 1 ? parts[1] : null;

		if (id !== null) {
			acc[id] = formValues[key];
		} else {
			console.warn(`Key "${key}" does not contain an underscore.`);
		}

		return acc;
	}, {});
